// @ts-ignore
import { METRIC_TYPE } from "../../constants";

export type MasonCollinsSupplementItem = {
  INDEX: number;
  TITLE: string;
  BULLETS: string[];
};

export type MasonCollinsSupplementRow = {
  INDEX: number;
  TITLE: string;
  IMPROVE_AREAS: MasonCollinsSupplementItem[];
};

const PERFORMANCE_SUPPORT_TITLE = "Performance Support";

// Bullet text is verbatim from the Mason Collins PDF.
// Supplement names are corrected for obvious typos in the source PDF
// (e.g. "Magneisum" / "Magnesiumm" -> "Magnesium").
//
// Cross-report aliases: when the same conceptual metric ships under
// different METRIC_TYPE values for RMR vs AMR (e.g. "Metabolic rate" is
// METRIC_TYPE.RMR=2 on RMR, METRIC_TYPE.METABOLIC_RATE=11 on AMR), both
// keys are registered below pointing at the SAME row so the Performance
// Support card renders identically on either report.
const METABOLIC_RATE_ROW: MasonCollinsSupplementRow = {
  INDEX: 2,
  TITLE: PERFORMANCE_SUPPORT_TITLE,
  IMPROVE_AREAS: [
    {
      INDEX: 1,
      TITLE: "Active B Complex",
      BULLETS: ["Support energy production", "Improve metabolic activity"],
    },
    {
      INDEX: 2,
      TITLE: "Creatine Monohydrate",
      BULLETS: [
        "Increase lean muscle mass",
        "Support higher resting metabolic rate",
      ],
    },
    {
      INDEX: 3,
      TITLE: "Performance Multi",
      BULLETS: ["Fill micronutrient deficiencies impacting metabolism"],
    },
  ],
};

export const MASON_COLLINS_SUPPLEMENT_DATA: Record<
  number,
  MasonCollinsSupplementRow
> = {
  [METRIC_TYPE.SDNN]: {
    INDEX: 2,
    TITLE: PERFORMANCE_SUPPORT_TITLE,
    IMPROVE_AREAS: [
      {
        INDEX: 1,
        TITLE: "Magnesium",
        BULLETS: [
          "Promote parasympathetic (rest-and-digest) activity",
          "Reduce stress response and cortisol",
        ],
      },
      {
        INDEX: 2,
        TITLE: "Omega-3 Fatty Acids",
        BULLETS: [
          "Improve heart rate variability",
          "Reduce chronic inflammation and stress signaling",
        ],
      },
      {
        INDEX: 3,
        TITLE: "Sleep Restore",
        BULLETS: [
          "Improve sleep quality and recovery",
          "Enhance parasympathetic activation overnight",
        ],
      },
    ],
  },
  [METRIC_TYPE.VENTILATION_EFFICIENCY]: {
    INDEX: 2,
    TITLE: PERFORMANCE_SUPPORT_TITLE,
    IMPROVE_AREAS: [
      {
        INDEX: 1,
        TITLE: "Magnesium",
        BULLETS: [
          "Relax airway smooth muscle",
          "Support efficient breathing patterns",
          "Reduce respiratory muscle fatigue",
        ],
      },
      {
        INDEX: 2,
        TITLE: "Omega-3 Fatty Acids",
        BULLETS: [
          "Reduce airway inflammation",
          "Improve oxygen exchange efficiency",
          "Support lung tissue health",
        ],
      },
      {
        INDEX: 3,
        TITLE: "Performance Multi",
        BULLETS: [
          "Support overall respiratory enzyme function",
          "Provide antioxidants for lung protection",
          "Fill micronutrient gaps impacting breathing",
        ],
      },
    ],
  },
  [METRIC_TYPE.BREATHING_STABILITY]: {
    INDEX: 2,
    TITLE: PERFORMANCE_SUPPORT_TITLE,
    IMPROVE_AREAS: [
      {
        INDEX: 1,
        TITLE: "Magnesium",
        BULLETS: [
          "Regulate neuromuscular control of breathing",
          "Promote relaxation of overactive breathing patterns",
        ],
      },
      {
        INDEX: 2,
        TITLE: "Active B Complex",
        BULLETS: [
          "Support nervous system signaling",
          "Improve brain-body coordination",
        ],
      },
      {
        INDEX: 3,
        TITLE: "Omega-3 Fatty Acids",
        BULLETS: [
          "Support brain function and autonomic regulation",
          "Improve vagal tone",
        ],
      },
    ],
  },
  [METRIC_TYPE.HEART_FITNESS]: {
    INDEX: 2,
    TITLE: PERFORMANCE_SUPPORT_TITLE,
    IMPROVE_AREAS: [
      {
        INDEX: 1,
        TITLE: "Magnesium",
        BULLETS: [
          "Improve autonomic balance",
          "Increase HRV through relaxation pathways",
        ],
      },
      {
        INDEX: 2,
        TITLE: "Omega-3 Fatty Acids",
        BULLETS: [
          "Support cardiovascular and nervous system function",
          "Improve vagal tone",
        ],
      },
      {
        INDEX: 3,
        TITLE: "Sleep Restore",
        BULLETS: [
          "Enhance recovery during sleep",
          "Improve overnight HRV",
        ],
      },
    ],
  },
  // "Metabolic rate" — RMR report ships METRIC_TYPE.RMR=2 for this metric.
  // Same row aliased to METRIC_TYPE.METABOLIC_RATE=11 below for AMR reports.
  [METRIC_TYPE.RMR]: METABOLIC_RATE_ROW,
  [METRIC_TYPE.METABOLIC_RATE]: METABOLIC_RATE_ROW,
  [METRIC_TYPE.FATBURN_EFFICIENCY]: {
    INDEX: 2,
    TITLE: PERFORMANCE_SUPPORT_TITLE,
    IMPROVE_AREAS: [
      {
        INDEX: 1,
        TITLE: "NAD+ Support",
        BULLETS: [
          "Improve cellular energy production",
          "Support mitochondrial repair and function",
        ],
      },
      {
        INDEX: 2,
        TITLE: "Creatine Monohydrate",
        BULLETS: [
          "Improve cellular energy availability",
          "Enhance high-energy phosphate systems",
        ],
      },
      {
        INDEX: 3,
        TITLE: "Performance Multi",
        BULLETS: [
          "Support enzymes involved in energy production",
          "Improve nutrient-driven metabolism",
        ],
      },
    ],
  },
  [METRIC_TYPE.METABOLIC_DYSFUNCTION_RISK]: {
    INDEX: 2,
    TITLE: PERFORMANCE_SUPPORT_TITLE,
    IMPROVE_AREAS: [
      {
        INDEX: 1,
        TITLE: "Metabolic Support (Berberine)",
        BULLETS: [
          "Improve insulin sensitivity",
          "Support glucose regulation",
          "Enhance fat utilization",
        ],
      },
      {
        INDEX: 2,
        TITLE: "Omega-3 Fatty Acids",
        BULLETS: [
          "Improve metabolic signaling",
          "Reduce insulin resistance",
        ],
      },
      {
        INDEX: 3,
        TITLE: "Magnesium",
        BULLETS: [
          "Support glucose metabolism",
          "Improve insulin function",
        ],
      },
    ],
  },
  [METRIC_TYPE.RESPIRATORY_CAPACITY]: {
    INDEX: 2,
    TITLE: PERFORMANCE_SUPPORT_TITLE,
    IMPROVE_AREAS: [
      {
        INDEX: 1,
        TITLE: "Magnesium",
        BULLETS: [
          "Improve diaphragm relaxation and contraction",
          "Support lung expansion mechanics",
        ],
      },
      {
        INDEX: 2,
        TITLE: "Omega-3 Fatty Acids",
        BULLETS: [
          "Reduce pulmonary inflammation",
          "Support lung elasticity",
        ],
      },
      {
        INDEX: 3,
        TITLE: "Vitamin D + K2",
        BULLETS: [
          "Support immune function in lung tissue",
          "Reduce risk of respiratory compromise",
        ],
      },
    ],
  },
  [METRIC_TYPE.OXYGEN_DELIVERY]: {
    INDEX: 2,
    TITLE: PERFORMANCE_SUPPORT_TITLE,
    IMPROVE_AREAS: [
      {
        INDEX: 1,
        TITLE: "Omega-3 Fatty Acids",
        BULLETS: [
          "Improve blood flow and circulation",
          "Support endothelial function",
        ],
      },
      {
        INDEX: 2,
        TITLE: "Vitamin D + K2",
        BULLETS: [
          "Support cardiovascular health",
          "Improve vascular integrity",
        ],
      },
      {
        INDEX: 3,
        TITLE: "Creatine Monohydrate",
        BULLETS: ["Enhance oxygen utilization at the muscle level"],
      },
    ],
  },
  // AMR-specific metric. Recovery Capacity is supported by peptides rather
  // than micronutrients — only 2 cards instead of the typical 3.
  [METRIC_TYPE.RECOVERY_CAPACITY]: {
    INDEX: 2,
    TITLE: PERFORMANCE_SUPPORT_TITLE,
    IMPROVE_AREAS: [
      {
        INDEX: 1,
        TITLE: "BPC-157",
        BULLETS: [
          "Support tissue repair and healing",
          "Improve gut integrity",
          "Reduce inflammation",
        ],
      },
      {
        INDEX: 2,
        TITLE: "Growth Hormone Secretagogues",
        BULLETS: [
          "Improve recovery and sleep quality",
          "Support body composition",
          "Enhance cellular repair",
        ],
      },
    ],
  },
};
